import * as backend from '@/api/backend';
import { checkResponseStatus } from '@/util/check';
import { formatUserForEdit, formatUserForApi } from '@/util/coerce';

let pendingUserSave = Promise.resolve();

const defaultUser = {
  id: null,
  user_id: '',
  firstName: '',
  lastName: '',
  displayIdentity: '',
  birthday: '2019-11-10',
  description: '',
  profileMailAddress: '',
  isDebugUser: false,
  debugPremium: false,
  timestamp: '',
  phone_number: '+',
  longitude: '',
  latitude: '',
  device: '',
  date: '',
  gender: '',
  question: '',
  question_webapp: '',
  userImageUrl: '',
  userBannerImageUrl: '',
  interests: [],
  hobbys: [],
  tickets: [],
  moderationStatus: 'active',
  moderationUpdatedAt: null,
  accountClosed: false,
  identityPermanentlyBlocked: false,
  memberInviteQuotaBonus: 0,
  referralCount: 0,
  inviteQuotaLabel: '',
  inviteQuotaPremiumLabel: null,
  promoter: null,
};

export default {
  state: {
    users: [],
    loading: false,
    error: false,
    userEdit: {
      ...defaultUser,
    },
    unsavedUsers: {},
    defaultUser,
  },
  mutations: {
    fetch(state) {
      state.loading = true;
    },
    errorUsers(state) {
      state.loading = false;
      state.error = true;
    },
    setUsers(state, users) {
      const formattedUsers = users.map((user) => ({
        userID: user.id,
        ...user,
        isDebugUser: Number(user.isDebugUser) === 1,
        debugPremium: Number(user.debugPremium) === 1,
        moderationStatus: user.moderationStatus || user.moderation_status || 'active',
        moderationUpdatedAt: user.moderationUpdatedAt ?? user.moderation_updated_at ?? null,
      }));
      state.users = formattedUsers;
      state.loading = false;
      state.error = false;
    },
    updateUserInList(state, user) {
      const id = Number(user.id);
      state.users = state.users.map((entry) => (
        Number(entry.id) === id ? { ...entry, ...user } : entry
      ));
    },
    editUser(state, user) {
      if (!user) {
        state.userEdit = { ...state.defaultUser };
        return;
      }
      const formattedUser = formatUserForEdit(user);
      state.userEdit = {
        ...JSON.parse(JSON.stringify(state.defaultUser)),
        ...formattedUser,
      };
    },
    updateUser(state, user) {
      const formattedUser = formatUserForEdit(user);
      Object.keys(user).forEach((key) => {
        state.userEdit[key] = formattedUser[key] !== undefined
          ? formattedUser[key]
          : user[key];
      });
    },
    addUnsavedUser(state, user) {
      state.unsavedUsers = {
        ...state.unsavedUsers,
        [user.id]: { ...user },
      };
    },
    removeUnsavedUser(state, user) {
      state.unsavedUsers = {
        ...state.unsavedUsers,
        [user.id]: undefined,
      };
    },
  },
  actions: {
    async fetchUsers({ commit }) {
      commit('fetch');
      try {
        const response = await backend.getUsers();
        checkResponseStatus(200, response);
        const json = response.data;
        commit('setUsers', json);
      } catch (e) {
        console.error(e);
        commit('errorUsers');
      }
    },
    async createUser({ commit }, { firebaseEmail, user }) {
      commit('fetch');
      const body = {
        ...user,
        firebaseEmail,
      };
      const response = await backend.postUser(body);
      await checkResponseStatus(201, response);

      const { userID, error } = response.data;
      if (error && error.message) {
        throw new Error(error.message);
      }
      return userID;
    },
    async saveUser({ commit, state }, payload) {
      const user = payload?.user ?? payload;
      const includeDisplayIdentity = payload?.includeDisplayIdentity === true;
      const memberInviteQuotaBonusOverride = payload?.memberInviteQuotaBonusOverride;
      const formattedForLock = formatUserForEdit(user);
      if (formattedForLock.accountClosed || state.userEdit?.accountClosed) {
        throw new Error('Konto ist geschlossen und kann nicht bearbeitet werden.');
      }

      const runSave = async () => {
        try {
          const formattedUser = formatUserForEdit(user);
          const response = await backend.patchUser(
            formattedUser.id,
            formatUserForApi(user, {
              includeDisplayIdentity,
              memberInviteQuotaBonusOverride,
            }),
          );
          await checkResponseStatus(204, response);

          const existing = state.users.find(
            (entry) => Number(entry.id) === Number(formattedUser.id),
          );
          const listUser = { ...formattedUser };
          if (!includeDisplayIdentity && existing?.displayIdentity != null) {
            listUser.displayIdentity = existing.displayIdentity;
          }
          commit('updateUserInList', listUser);
          commit('removeUnsavedUser', user);
        } catch (err) {
          commit('addUnsavedUser', user);
          throw err;
        }
      };

      pendingUserSave = pendingUserSave.catch(() => {}).then(runSave);
      return pendingUserSave;
    },
  },
  namespaced: true,
};
